import { useId, type ReactNode } from 'react';

import Text from '@/components/ui/text';
import { Switch } from '@/components/ui/switch';
import {
  type FormSpacing,
  formSpacing,
  validationTextClasses,
} from '@/theme/size-tokens';

export type FormSwitchProps = {
  /** Checked state (controlled) */
  checked?: boolean;
  /** Called with the new checked value */
  onCheckedChange?: (checked: boolean) => void;
  /** Legacy onChange handler for backward compat */
  onChange?: (e: { target: { checked: boolean } }) => void;
  /** Disables switch interaction */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Label text displayed next to the switch */
  label: string;
  /** Helper text displayed below the switch */
  helperText?: string;
  /** Error message to display */
  error?: string;
  /** HTML id attribute for the switch */
  id?: string;
  /** Spacing between switch/label and helper text */
  spacing?: FormSpacing;
  /** Associates the switch with additional descriptive text */
  'aria-describedby'?: string;
  /** Overrides the accessible name announced by screen readers */
  'aria-label'?: string;
  /** Optional content rendered to the right of the label (e.g. an info tooltip) */
  labelSuffix?: ReactNode;
};

function FormSwitch({
  label,
  helperText,
  error,
  spacing = 'xs',
  id: providedId,
  disabled,
  className,
  checked,
  onCheckedChange,
  onChange,
  'aria-describedby': userAriaDescribedby,
  'aria-label': ariaLabel,
  labelSuffix,
}: Readonly<FormSwitchProps>) {
  const autoId = useId();
  const inputId = providedId ?? autoId;
  const messageId = `${inputId}-message`;
  const hasMessage = !!(error ?? helperText);

  const ariaDescribedby =
    [hasMessage ? messageId : undefined, userAriaDescribedby]
      .filter(Boolean)
      .join(' ') || undefined;

  const handleCheckedChange = (value: boolean) => {
    onCheckedChange?.(value);
    onChange?.({ target: { checked: value } });
  };

  return (
    <div className={`flex flex-col ${formSpacing[spacing]}`}>
      <div className="inline-flex w-fit items-center gap-2">
        <Switch
          id={inputId}
          checked={checked}
          onCheckedChange={handleCheckedChange}
          disabled={disabled}
          className={className}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
        />
        <label
          htmlFor={inputId}
          className="text-foreground inline-flex cursor-pointer items-center gap-2 text-sm"
        >
          <Text variant={disabled ? 'muted' : 'body'} size="sm" as="span">
            {label}
          </Text>
        </label>
        {labelSuffix}
      </div>

      {hasMessage && (
        <p
          id={messageId}
          className={`text-sm ${error ? validationTextClasses.error : validationTextClasses.neutral}`}
          aria-live="polite"
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  );
}

export default FormSwitch;
